// ============================================================
// ExportDialog - Modal for selecting which session data to export.
// Presents checkboxes for attack profiles, target tabs, templates,
// and narrative logs with item counts for each section.
// ============================================================
import CharacterExportDialog from './CharacterExportDialog';
import { useState } from 'react';
import type { Tab, AtkProfiles } from '../types';
import common from '../styles/common.module.css';
import { countOf } from '../utils/countOf';
import { SectionFooter, SectionRows, type SectionRowSpec } from './ImportExportParts';
import styles from '../styles/ImportExportDialog.module.css';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import clsx from '../utils/clsx';

export interface ExportSelections {
  attackProfiles: boolean;
  targetTabs: boolean;
  targetTemplates: boolean;
  narrativeLogs: boolean;
  /** v4: wounds, conditions, crits and ammo. Off → a clean template. */
  combatState: boolean;
  loadouts: boolean;
  /** v7.51.0 - the map itself, the saved library, and where everyone stands. */
  battlefield: boolean;
  mapLibrary: boolean;
  tokenPositions: boolean;
  waitingRooms: boolean;
  /** v8.363.10 - handouts: titles, folders, tags, notes and links. Never the files. */
  handouts: boolean;
}

interface Props {
  tabs: Tab[];
  /** v4 roster - how many combatants the file will carry. */
  combatantCount?: number;
  /** Who the characters-only export starts with ticked: the roster's characters. */
  characterIds?: string[];
  /** `editSheets` is closed to this viewer: no library save from the characters-only export. */
  sheetsLocked?: boolean;
  loadoutCount?: number;
  handoutCount?: number;
  hasTerrain?: boolean;
  mapCount?: number;
  tokenCount?: number;
  roomCount?: number;
  atkProfiles: AtkProfiles;
  tgtTemplates: Record<string, unknown>;
  onExport: (selections: ExportSelections) => void;
  onCancel: () => void;
}

type Row = SectionRowSpec<keyof ExportSelections>;

const INITIAL: ExportSelections = {
  attackProfiles: true,
  targetTabs: true,
  targetTemplates: true,
  narrativeLogs: true,
  combatState: true,
  loadouts: true,
  battlefield: true,
  mapLibrary: true,
  // ON, because the common use of this dialog is saving YOUR OWN session
  // and a file without positions reloads as everyone in a ring in the
  // middle - reported from play (2026-08-19). The original reasoning was
  // about handing a template to another GM, which the checkbox still
  // serves; it just should not be the default for everybody else.
  tokenPositions: true,
  // OFF BY DEFAULT, and deliberately: the file may go to a player,
  // and an ambush they can read is not an ambush.
  waitingRooms: false,
  handouts: true,
};

/** The people: profiles, tabs, templates and the narrative the tabs carry. */
function peopleRows(p: Props, selections: ExportSelections): Row[] {
  const profileNames = Object.keys(p.atkProfiles.list);
  const nonEmptyWeapons = profileNames.reduce(
    (sum, name) => sum + (p.atkProfiles.list[name]?.filter((s) => !s.isEmpty).length ?? 0),
    0,
  );
  const narrativeCount = p.tabs.reduce((sum, t) => sum + (t.narrativeLog?.length ?? 0), 0);
  return [
    {
      key: 'attackProfiles',
      id: 'exp-profiles',
      label: 'Attack Profiles',
      count: `${countOf(profileNames.length, 'profile')}, ${countOf(nonEmptyWeapons, 'weapon')}`,
    },
    { key: 'targetTabs', id: 'exp-tabs', label: 'Target Tabs', count: countOf(p.tabs.length, 'tab') },
    {
      key: 'targetTemplates',
      id: 'exp-templates',
      label: 'Target Templates',
      count: countOf(Object.keys(p.tgtTemplates).length, 'template'),
    },
    {
      key: 'narrativeLogs',
      id: 'exp-narrative',
      label: 'Narrative Logs',
      available: selections.targetTabs,
      count: countOf(narrativeCount, 'entry', 'entries'),
    },
  ];
}

/** What rides with the roster: its combat state, its survival rules, its kits. */
function rosterRows(p: Props, peopleSelected: boolean): Row[] {
  return [
    // v4: the roster carries wounds/conditions/ammo. Unchecking this
    // exports a clean template - the version you hand another GM.
    {
      key: 'combatState',
      id: 'exp-state',
      label: 'Combat State',
      available: peopleSelected,
      count: `${countOf(p.combatantCount ?? 0, 'combatant')} - wounds, conditions, ammo`,
    },
    { key: 'loadouts', id: 'exp-loadouts', label: 'Loadouts', count: countOf(p.loadoutCount ?? 0, 'kit') },
  ];
}

/**
 * The battlefield. Terrain and library are the usual thing to hand
 * another GM; handouts and waiting rooms are offered only when there are
 * some, and the rooms default off because they are hidden.
 */
function battlefieldRows(p: Props): Row[] {
  const { mapCount = 0, tokenCount = 0, roomCount = 0, handoutCount = 0 } = p;
  return [
    {
      key: 'battlefield',
      id: 'exp-battlefield',
      label: 'Battlefield terrain',
      count: p.hasTerrain ? 'the map in play' : 'none',
    },
    { key: 'mapLibrary', id: 'exp-map-library', label: 'Saved map library', count: countOf(mapCount, 'map') },
    {
      key: 'handouts',
      id: 'exp-handouts',
      testId: 'export-handouts',
      label: (
        <>
          Handouts - titles, notes and links; <b>not the files</b>, which stay with this session
        </>
      ),
      count: countOf(handoutCount, 'handout'),
      show: handoutCount > 0,
    },
    {
      key: 'waitingRooms',
      id: 'exp-waiting-rooms',
      testId: 'export-waiting-rooms',
      label: (
        <>
          Waiting rooms - <b>hidden</b>, so leave this off if a player will read the file
        </>
      ),
      count: countOf(roomCount, 'room'),
      show: roomCount > 0,
    },
    {
      key: 'tokenPositions',
      id: 'exp-token-positions',
      label: 'Where everyone is standing',
      count: countOf(tokenCount, 'marker'),
    },
  ];
}

export default function ExportDialog(props: Props) {
  const { onExport, onCancel } = props;
  const backdrop = useModalBackdrop(onCancel);
  useEscapeKey(true, onCancel); // Escape cancels, as a click outside does (2026-10-23).
  const [selections, setSelections] = useState<ExportSelections>(INITIAL);
  // Characters only: a different file, which JOINS a roster on import where a session file replaces it.
  const [characters, setCharacters] = useState(false);
  const toggle = (key: keyof ExportSelections) => setSelections((prev) => ({ ...prev, [key]: !prev[key] }));

  // The roster rides with either "people" section - see useImportExport.
  const peopleSelected = selections.attackProfiles || selections.targetTabs;
  const noneSelected = !selections.attackProfiles && !selections.targetTabs && !selections.targetTemplates;
  const rows = [...peopleRows(props, selections), ...rosterRows(props, peopleSelected), ...battlefieldRows(props)];

  if (characters)
    return (
      <CharacterExportDialog
        initialIds={props.characterIds ?? []}
        canSaveToLibrary={!props.sheetsLocked}
        onClose={onCancel}
      />
    );
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div className={common.modalContent} ref={backdrop.boxRef}>
        <h2 className={styles.title}>Export Session Data</h2>
        <SectionRows rows={rows} selections={selections} onToggle={toggle} />
        <button
          type="button"
          className={clsx(common.btnSmall, styles.door)}
          data-testid="export-characters"
          onClick={() => setCharacters(true)}
        >
          People only, as they stand...
        </button>
        <SectionFooter
          verb="Export"
          onCancel={onCancel}
          onConfirm={() => onExport(selections)}
          disabled={noneSelected}
        />
      </div>
    </div>
  );
}
